import { Injectable, inject } from '@angular/core';
import { Observable, switchMap, of, tap, map, finalize, BehaviorSubject, take, throwError, catchError } from 'rxjs';
import * as Sentry from '@sentry/angular';
import { Store } from '@ngrx/store';
import { Router } from '@angular/router';
import { AuthService } from '../auth.service';
import { GrandCompteService } from '../grand-compte.service';
import { GrandCompteDnsService } from '../grand-compte-dns.service';
import { WalletService } from '../wallet.service';
import { User, GrandCompte } from '@core/models/user.model';
import { LoginRequest } from '@core/models/auth.model';
import { Currency } from '@core/models';
import * as WalletActions from '@shared/store/wallets/wallet.actions';
import * as WalletSelectors from '@shared/store/wallets/wallet.selectors';
import * as CampaignActions from '@features/campaigns/store/campaign.actions';
import { FacturationData, WalletData } from '@shared/store/wallets/wallet.reducer';
import { captureApiException } from '@core/utils/sentry.utils';

@Injectable({
    providedIn: 'root',
})
export class AppFacade {
    private authService = inject(AuthService);
    private store = inject(Store);
    private router = inject(Router);
    private grandCompteService = inject(GrandCompteService);
    private grandCompteDnsService = inject(GrandCompteDnsService);
    private walletService = inject(WalletService);

    private loadingSubject = new BehaviorSubject<boolean>(false);
    private selectedCurrencySubject = new BehaviorSubject<Currency>(Currency.EUR);

    // Expose tous les observables nécessaires
    currentUser$ = this.authService.currentUser$;
    isAuthenticated$ = this.authService.isAuthenticated$;
    currentGrandCompte$ = this.grandCompteService.currentGc$;
    advertisers$ = this.currentGrandCompte$.pipe(
        map((grandCompte) => (grandCompte ? [...grandCompte.advertisers].sort((a, b) => a.advertiser_name.localeCompare(b.advertiser_name)) : [])),
    );
    loading$ = this.loadingSubject.asObservable();
    currentWallet$ = this.store.select(WalletSelectors.selectCurrentWallet);
    currentFacturation$ = this.store.select(WalletSelectors.selectCurrentFacturation);
    facturations$ = this.store.select(WalletSelectors.selectAllFacturations);
    wallets$ = this.store.select(WalletSelectors.selectAllWallets);
    selectedCurrency$ = this.selectedCurrencySubject.asObservable();

    constructor() {
        if (this.authService.isAuthenticated && this.grandCompteService.currentGrandCompte()) {
            this.initializeCurrency();
        }
    }

    getCurrencySymbol(): string {
        const currency = this.selectedCurrencySubject.value;
        switch (currency) {
            case Currency.EUR:
                return '€';
            case Currency.USD:
                return '$';
            case Currency.GBP:
                return '£';
            case Currency.CAD:
                return 'CA$';
            default:
                return '';
        }
    }

    login(credentials: LoginRequest): Observable<boolean> {
        this.loadingSubject.next(true);

        return this.authService.login(credentials).pipe(
            switchMap((tokenStored) => {
                if (!tokenStored) {
                    return of(false);
                }

                return this.authService.reloadUserData().pipe(
                    switchMap((user) => {
                        if (!user) {
                            return throwError(() => new Error('reload-user-data-failed'));
                        }

                        const selectedGrandCompte = this.selectInitialGrandCompte(user);

                        if (selectedGrandCompte) {
                            return this.setupGrandCompte(selectedGrandCompte).pipe(map(() => true));
                        }

                        return of(true);
                    }),
                );
            }),
            catchError((error) => {
                captureApiException(error, { step: 'login-reload-user-data' });

                if (error?.message === 'reload-user-data-failed') {
                    return throwError(() => error);
                }

                return of(false);
            }),
            finalize(() => {
                this.loadingSubject.next(false);
            }),
        );
    }

    sendResetPasswordEmail(email: string, gcId: number): Observable<any> {
        return this.authService.forgotPassword(email, gcId);
    }

    changeGrandCompte(grandCompteId: number): Observable<void> {
        const user = this.authService.currentUserValue;
        if (!user) {
            return of(undefined);
        }

        const grandCompte = user.grand_comptes.find((gc) => gc.id === grandCompteId);
        if (!grandCompte) {
            return of(undefined);
        }

        // Clear cached data when changing grand compte
        this.store.dispatch(WalletActions.clearWalletsAndFacturations());
        this.store.dispatch(CampaignActions.resetCampaigns());

        return this.setupGrandCompteWithTheme(grandCompte).pipe(
            tap(() => {
                Sentry.addBreadcrumb({
                    category: 'navigation',
                    message: `GrandCompte switched to: ${grandCompte.name} (id: ${grandCompteId})`,
                    level: 'info',
                });
            }),
        );
    }

    changeCurrency(newCurrency: Currency): Observable<void> {
        const currentGc = this.grandCompteService.currentGrandCompte();
        if (!currentGc) {
            return of(undefined);
        }

        this.selectedCurrencySubject.next(newCurrency);
        localStorage.setItem('selectedCurrency', newCurrency);

        // The API converts the balances: ask it again in the new currency instead of converting the store here.
        this.walletService.loadWalletsForGrandCompte(currentGc, newCurrency);
        // The list is cached in the store: drop it so the next visit refetches the amounts in the new currency.
        this.store.dispatch(CampaignActions.invalidateCampaigns());

        return of(undefined);
    }

    private normalizeCurrency(value: Currency | string): Currency {
        switch (String(value).toUpperCase()) {
            case 'USD':
            case '$':
                return Currency.USD;
            case 'GBP':
            case '£':
                return Currency.GBP;
            case 'CAD':
            case 'CA$':
                return Currency.CAD;
            default:
                return Currency.EUR;
        }
    }

    /** Persists the communication language (emails, PDFs) on the user's account (MBY-434). Never throws. */
    saveUserLocale(lang: string): Observable<boolean> {
        return this.authService.saveUserLocale(lang);
    }

    async logout(): Promise<void> {
        this.store.dispatch(WalletActions.clearWalletsAndFacturations());
        this.store.dispatch(CampaignActions.resetCampaigns());
        this.grandCompteService.clearCurrentGrandCompte();
        Sentry.setTag('grand_compte_id', '');
        Sentry.setTag('grand_compte_name', '');
        await this.authService.logout();
    }

    private setupGrandCompte(grandCompte: GrandCompte): Observable<void> {
        const user = this.authService.currentUserValue;
        if (user) {
            localStorage.setItem('userGrandComptes', JSON.stringify(user.grand_comptes));
            localStorage.setItem('currentGrandCompte', JSON.stringify(grandCompte));
        }

        Sentry.setTag('grand_compte_id', String(grandCompte.id));
        Sentry.setTag('grand_compte_name', grandCompte.name);

        this.grandCompteService.setCurrentGrandCompte(grandCompte.id);

        const currentDnsGc = this.grandCompteDnsService.currentGcByDns();
        if (!currentDnsGc || currentDnsGc.id !== grandCompte.id) {
            this.grandCompteDnsService.changeGrandCompte(grandCompte.id);
            console.warn('GC DNS ne correspond pas au GC sélectionné');
        }

        const newCurrency = this.normalizeCurrency(this.grandCompteDnsService.currentGCCurrency());
        this.selectedCurrencySubject.next(newCurrency);
        localStorage.setItem('selectedCurrency', newCurrency);

        const currency = newCurrency;

        return this.store.select(WalletSelectors.selectWalletState).pipe(
            take(1),
            switchMap((state) => {
                const walletsCount = Object.keys(state.walletsMap).length;
                const facturationsCount = Object.keys(state.facturationsMap).length;

                if (walletsCount > 0 || facturationsCount > 0) {
                    const firstWalletData = Object.values(state.walletsMap)[0];
                    if (firstWalletData && firstWalletData.grandCompteId === grandCompte.id) {
                        return of(undefined);
                    }
                }

                return this.walletService.loadWalletsAndFacturationsForGrandCompte(grandCompte, currency).pipe(map(() => undefined));
            }),
        );
    }

    private setupGrandCompteWithTheme(grandCompte: GrandCompte): Observable<void> {
        const user = this.authService.currentUserValue;
        if (user) {
            localStorage.setItem('userGrandComptes', JSON.stringify(user.grand_comptes));
            localStorage.setItem('currentGrandCompte', JSON.stringify(grandCompte));
        }

        Sentry.setTag('grand_compte_id', String(grandCompte.id));
        Sentry.setTag('grand_compte_name', grandCompte.name);

        this.grandCompteService.setCurrentGrandCompte(grandCompte.id);

        this.grandCompteDnsService.changeGrandCompte(grandCompte.id);

        const newCurrency = this.normalizeCurrency(this.grandCompteDnsService.currentGCCurrency());
        this.selectedCurrencySubject.next(newCurrency);
        localStorage.setItem('selectedCurrency', newCurrency);

        return this.walletService.loadWalletsAndFacturationsForGrandCompte(grandCompte, newCurrency).pipe(map(() => undefined));
    }

    private selectInitialGrandCompte(user: User): GrandCompte | null {
        if (!user.grand_comptes?.length) {
            return null;
        }

        const grandCompteDns = localStorage.getItem('currentGCByDns');

        if (grandCompteDns) {
            try {
                const gcDns = JSON.parse(grandCompteDns);
                const found = user.grand_comptes.find((gc) => gc.id === gcDns.id);
                if (found) {
                    return found;
                }
            } catch (error) {
                console.error('Error parsing grandCompteDns:', error);
            }
        }

        return user.grand_comptes[0];
    }

    async restoreUserSession(): Promise<void> {
        const user = this.authService.currentUserValue;
        const storedGcId = this.grandCompteService.currentGrandCompteId();

        if (user && storedGcId) {
            const grandCompte = user.grand_comptes.find((gc) => gc.id === storedGcId);
            if (grandCompte) {
                // The data will be loaded from store or API if needed
                await this.setupGrandCompte(grandCompte).toPromise();
            }
        }
    }

    initializeCurrency(): void {
        const savedCurrency = localStorage.getItem('selectedCurrency') as Currency;
        if (savedCurrency && Object.values(Currency).includes(savedCurrency)) {
            this.selectedCurrencySubject.next(savedCurrency);
        } else {
            // Utiliser la devise du grand compte DNS par défaut
            const dnsCurrency = this.grandCompteDnsService.currentGCCurrency();
            if (dnsCurrency) {
                this.selectedCurrencySubject.next(dnsCurrency);
            }
        }
    }

    // Méthodes utilitaires
    get currentUser(): User | null {
        return this.authService.currentUserValue;
    }

    get isAuthenticated(): boolean {
        return this.authService.isAuthenticated;
    }

    get currentGrandCompteId(): number | null {
        return this.grandCompteService.currentGrandCompteId();
    }

    get currentGrandCompte(): GrandCompte | null {
        return this.grandCompteService.currentGrandCompte();
    }

    get selectedCurrency(): Currency {
        return this.selectedCurrencySubject.value;
    }

    hasRole(role: string): boolean {
        return this.authService.hasRole(role);
    }

    getUserGrandComptes(): GrandCompte[] {
        const sortedGrandComptes = this.currentUser?.grand_comptes
            ? [...this.currentUser.grand_comptes].sort((a, b) => a.name.localeCompare(b.name))
            : [];
        return sortedGrandComptes;
    }

    get currentFacturation(): FacturationData | null {
        let facturation: FacturationData | null = null;
        this.currentFacturation$.pipe(take(1)).subscribe((f) => (facturation = f));
        return facturation;
    }

    get isAdmin(): boolean {
        return this.currentUser?.isAdmin() || false;
    }

    get canReloadCampaignData(): boolean {
        return this.currentUser?.canReloadCampaignData() || false;
    }

    get canEditAiSummary(): boolean {
        return this.currentUser?.canEditAiSummary() || false;
    }

    get canManageGeoPrompts(): boolean {
        return this.currentUser?.canManageGeoPrompts() || false;
    }

    reloadUserData(): Observable<User | null> {
        return this.authService.reloadUserData().pipe(
            tap((user) => {
                if (!user) {
                    return;
                }

                localStorage.setItem('userGrandComptes', JSON.stringify(user.grand_comptes));

                const storedGcId = this.grandCompteService.currentGrandCompteId();
                const currentGc = storedGcId ? user.grand_comptes.find((gc) => gc.id === storedGcId) : null;
                if (currentGc) {
                    localStorage.setItem('currentGrandCompte', JSON.stringify(currentGc));
                    this.grandCompteService.setCurrentGrandCompte(currentGc.id);
                }
            }),
        );
    }

    updateAdvertiserIndustry(clientId: string, industryId: number): void {
        this.authService.updateAdvertiserIndustry(clientId, industryId);
        this.grandCompteService.updateAdvertiserIndustry(clientId, industryId);
    }

    /**
     * Get wallet for a specific advertiser in the current grand compte
     */
    getWalletForAdvertiser(clientId: string): Observable<WalletData | null> {
        const currentGcId = this.currentGrandCompteId;
        if (!currentGcId) {
            return of(null);
        }

        return this.store.select(WalletSelectors.selectWalletByClientAndGrandCompte(clientId, currentGcId)).pipe(
            take(1),
            map((walletData) => {
                if (!walletData || !walletData.wallet) {
                    return null;
                }

                return walletData;
            }),
        );
    }

    /**
     * Facturation of an advertiser (current grand compte unless one is given), loading it first when the store doesn't have it yet
     */
    getFacturationForAdvertiser(clientId: string, grandCompteId: number | null = this.currentGrandCompteId): Observable<FacturationData | null> {
        if (!grandCompteId) {
            return of(null);
        }

        return this.walletService.getFacturationForAdvertiser(clientId, grandCompteId);
    }

    /**
     * Get all wallets for the current grand compte
     */
    getWalletsForCurrentGrandCompte(): Observable<WalletData[]> {
        const currentGcId = this.currentGrandCompteId;
        if (!currentGcId) {
            return of([]);
        }

        return this.store.select(WalletSelectors.selectWalletsByGrandCompte(currentGcId));
    }

    /**
     * Set the current wallet for payment purposes
     */
    setCurrentWalletForPayment(clientId: string): void {
        const currentGcId = this.currentGrandCompteId;
        if (currentGcId) {
            this.walletService.setCurrentWallet(clientId, currentGcId);
        }
    }
}
